import { dammCheckDigit, isValidDammNumber } from './damm.js';

/** 9 zufällige Ziffern + 1 Prüfziffer = 10 Ziffern (gerade Anzahl, passt zu Code 128 Set C). */
export const LABEL_RANDOM_DIGITS = 9;
export const LABEL_CODE_LENGTH = LABEL_RANDOM_DIGITS + 1;

/**
 * Liefert eine gleichverteilte Ziffer 0–9.
 * Die Zufallsquelle wird bewusst übergeben: Codes erzeugt **nur der Server** (mit `node:crypto`),
 * dieses Paket läuft auch in Browser und App und enthält deshalb keine eigene Zufallsquelle.
 */
export type RandomDigit = () => number;

/** Entfernt Leerzeichen (auch geschützte) und Bindestriche (auch typografische). */
export function normalizeLabelCode(input: string): string {
  return input.replace(/[\s\-‐-―−]/g, '');
}

/** Prüft Form (10 Ziffern) und Prüfziffer. Der Startwert gehört zur Organisation. */
export function isValidLabelCode(input: string, salt: number): boolean {
  const code = normalizeLabelCode(input);
  return code.length === LABEL_CODE_LENGTH && isValidDammNumber(code, salt);
}

/** Zeigt einen gültig geformten Code gruppiert an: „48213 90737“. */
export function formatLabelCode(code: string): string {
  const normalized = normalizeLabelCode(code);
  if (!/^\d{10}$/.test(normalized)) {
    throw new TypeError('Ein Etikettencode besteht aus genau 10 Ziffern.');
  }
  return `${normalized.slice(0, 5)} ${normalized.slice(5)}`;
}

/**
 * Bildet einen neuen Code aus 9 Zufallsziffern und der Prüfziffer.
 * Die Eindeutigkeit garantiert **nicht** diese Funktion, sondern der Datenbank-Index
 * `UNIQUE (event_id, label_code)` (eindeutig je Basar) mit Wiederholung bei Duplikat-Fehler.
 */
export function generateLabelCode(salt: number, randomDigit: RandomDigit): string {
  let digits = '';
  for (let i = 0; i < LABEL_RANDOM_DIGITS; i += 1) {
    const digit = randomDigit();
    if (!Number.isInteger(digit) || digit < 0 || digit > 9) {
      throw new RangeError('randomDigit muss eine Ziffer von 0 bis 9 liefern.');
    }
    digits += String(digit);
  }
  return `${digits}${String(dammCheckDigit(digits, salt))}`;
}

/** Fremde Codes (Entscheidung des Auftraggebers: vorhandene Etiketten übernehmen): Länge passt zu `sale_lines.code_text`. */
export const EXTERNAL_CODE_MIN_LENGTH = 4;
export const EXTERNAL_CODE_MAX_LENGTH = 30;

/**
 * Prüft einen fremden Code (z. B. EAN einer eigenen Etikettenrolle), den ein Verkäufer einem Artikel zuordnet: nur
 * Buchstaben, Ziffern und . _ + - (übliche Barcodes, keine Web-Adressen aus QR-Codes), 4 bis 30 Zeichen. Ein Code, der wie ein BARSO-Code oder dessen Tippfehler
 * aussieht (nach dem Entfernen von Leerzeichen und Bindestrichen 9 bis 11 Ziffern), ist ausgeschlossen: So kann ein
 * fremder Code nie mit einem BARSO-Code verwechselt werden, eine Ziffer zu viel oder zu wenig bleibt ein erkannter
 * Tippfehler, und die Suche weiß an der Form, in welcher Spalte sie sucht. EAN-8, UPC-A und EAN-13 bleiben möglich.
 */
export function isExternalCode(input: string): boolean {
  const code = normalizePoolInput(input).trim();
  return (
    new RegExp(
      `^[A-Za-z0-9._+\\-]{${String(EXTERNAL_CODE_MIN_LENGTH)},${String(EXTERNAL_CODE_MAX_LENGTH)}}$`,
    ).test(code) && !/^\d{9,11}$/.test(normalizeLabelCode(code))
  );
}

/**
 * Einheitliche Schreibweise fremder Codes beim Speichern UND beim Suchen: getrimmt, Großbuchstaben (Code 39 kennt nur
 * Großbuchstaben, von Hand Getipptes kommt oft klein), Bindestriche bleiben.
 */
export function canonicalExternalCode(input: string): string {
  return normalizePoolInput(input).trim().toUpperCase();
}

/**
 * Ordnet einen gescannten oder getippten Text ein: BARSO-Code (10 Ziffern, normalisiert) oder fremder Code (nur
 * einheitlich geschrieben, siehe `canonicalExternalCode`). `null`, wenn er keins von beiden sein kann.
 */
export function classifyScannedCode(
  input: string,
): { kind: 'label'; code: string } | { kind: 'external'; code: string } | null {
  const text = normalizePoolInput(input);
  const normalized = normalizeLabelCode(text);
  if (/^\d{10}$/.test(normalized)) return { kind: 'label', code: normalized };
  if (isExternalCode(text)) return { kind: 'external', code: canonicalExternalCode(text) };
  return null;
}

/**
 * Sieht der Text nach einem Hersteller-Barcode aus (gültige GTIN-8/12/13/14 mit Prüfziffer)? Ausgenommen sind Händler-
 * Hausnummern: in der 13-stelligen Form Präfix 02, 04 (UPC-A Nummernsystem 4) und 20–29; bei EAN-8 eine erste Ziffer 0
 * oder 2 (Kurznummern für den internen Gebrauch). Sonst steckt wahrscheinlich ein Etikett des Herstellers darin, das für
 * viele Stücke gleich ist: Die Oberfläche warnt davor (Eindeutigkeit je Basar!), verbietet es aber nicht.
 */
export function looksLikeManufacturerBarcode(input: string): boolean {
  const code = input.trim();
  if (!/^\d+$/.test(code) || ![8, 12, 13, 14].includes(code.length)) return false;
  if (!hasValidGtinCheckDigit(code)) return false;
  if (code.length === 8) return !['0', '2'].includes(code.charAt(0));
  const prefix =
    code.length === 14
      ? code.slice(1, 3)
      : code.length === 12
        ? `0${code.slice(0, 1)}`
        : code.slice(0, 2);
  const number = Number(prefix);
  return prefix !== '02' && prefix !== '04' && !(number >= 20 && number <= 29);
}

/**
 * Dauer-Etiketten je Person (Pool, `seller_label_codes`): von BARSO erzeugte Codes, die einer Person gehören und in jedem
 * Basar als fremder Code (`items.external_code`) zugeordnet werden können. Form: 12 Ziffern = reserviertes Präfix „99“ +
 * 9 Zufallsziffern + Damm-Prüfziffer (Startwert 0). 12 Ziffern liegen bewusst außerhalb der BARSO-Codes (10 Ziffern) und
 * ihrer Tippfehler (9 bis 11 Ziffern, siehe `isExternalCode`); es ist immer ein „externer“ Code.
 */
export const POOL_CODE_PREFIX = '99';
export const POOL_CODE_RANDOM_DIGITS = 9;
export const POOL_CODE_LENGTH = POOL_CODE_PREFIX.length + POOL_CODE_RANDOM_DIGITS + 1;
/** So oft wird neu gewürfelt, wenn zufällig eine gültige EAN/GTIN-Prüfziffer entsteht (Wahrscheinlichkeit je Versuch 1 : 10). */
const POOL_CODE_MAX_ROLLS = 200;

/** GS1-Prüfziffer einer reinen Ziffernfolge der Länge 8, 12, 13 oder 14 (Gewichte 3 und 1 von rechts). */
function hasValidGtinCheckDigit(digits: string): boolean {
  let sum = 0;
  for (let i = digits.length - 2, weight = 3; i >= 0; i -= 1, weight = 4 - weight) {
    sum += Number(digits.charAt(i)) * weight;
  }
  return (10 - (sum % 10)) % 10 === Number(digits.charAt(digits.length - 1));
}

/**
 * Prüft die Form eines Dauer-Etiketts: genau 12 Ziffern, Präfix „99“, gültige Damm-Prüfziffer und KEINE gültige GTIN-
 * Prüfziffer (so bleibt ein echter Hersteller-Barcode mit zufällig gleichem Anfang ein gewöhnlicher fremder Code). Das
 * ist nur die Form: Wem der Code gehört, entscheidet allein die Tabelle `seller_label_codes`.
 */
export function isPoolCode(input: string): boolean {
  const code = input.trim();
  return (
    code.length === POOL_CODE_LENGTH &&
    /^\d+$/.test(code) &&
    code.startsWith(POOL_CODE_PREFIX) &&
    isValidDammNumber(code, 0) &&
    !hasValidGtinCheckDigit(code)
  );
}

/**
 * Bildet einen neuen Pool-Code. Die Eindeutigkeit garantiert **nicht** diese Funktion, sondern der Datenbank-Index
 * `UNIQUE (code)` mit Wiederholung bei Duplikat-Fehler. Entsteht zufällig eine gültige GTIN-Prüfziffer, wird neu gewürfelt.
 */
export function generatePoolCode(randomDigit: RandomDigit): string {
  for (let roll = 0; roll < POOL_CODE_MAX_ROLLS; roll += 1) {
    let digits = POOL_CODE_PREFIX;
    for (let i = 0; i < POOL_CODE_RANDOM_DIGITS; i += 1) {
      const digit = randomDigit();
      if (!Number.isInteger(digit) || digit < 0 || digit > 9) {
        throw new RangeError('randomDigit muss eine Ziffer von 0 bis 9 liefern.');
      }
      digits += String(digit);
    }
    const code = `${digits}${String(dammCheckDigit(digits, 0))}`;
    if (!hasValidGtinCheckDigit(code)) return code;
  }
  throw new RangeError('Kein Pool-Code ohne gültige GTIN-Prüfziffer gefunden.');
}

/**
 * Normalisiert die Eingabe eines Dauer-Etiketts: Das Klartext-Etikett ist gruppiert gedruckt („9912 3456 7895“), und von
 * Hand wird es auch so abgetippt. Bestehen die Zeichen ohne Leerzeichen (auch geschützte) und Bindestriche aus einem
 * gültig geformten Pool-Code (`isPoolCode`), kommt genau dieser Code ohne Trenner zurück. Alles andere kommt UNVERÄNDERT
 * zurück: BARSO-Codes (10 Ziffern), Eingaben mit 9 bis 11 Ziffern (Tippfehler bleiben erkennbar) und fremde Codes mit
 * Bindestrich („AB-123“) werden nie angefasst. Einzige gemeinsame Stelle: `isExternalCode`, `canonicalExternalCode` und
 * `classifyScannedCode` rufen sie auf, damit Kasse (auch offline), Annahme, Rückgabe, Prüfen und Zuordnung gleich lesen.
 */
export function normalizePoolInput(input: string): string {
  const stripped = normalizeLabelCode(input);
  return stripped !== input && isPoolCode(stripped) ? stripped : input;
}

/** Zeigt einen Pool-Code gruppiert an: „9912 3456 7895“. */
export function formatPoolCode(code: string): string {
  const normalized = code.trim();
  if (!/^\d{12}$/.test(normalized)) {
    throw new TypeError('Ein Dauer-Etikett besteht aus genau 12 Ziffern.');
  }
  return `${normalized.slice(0, 4)} ${normalized.slice(4, 8)} ${normalized.slice(8)}`;
}
